
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNewsArticles } from '../../hooks/useNewsArticles';
import { newsCategories } from '../../mocks/localNews';
import { useSEO } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import TrendingTicker from '../home/components/TrendingTicker';
import SubmitNewsTip from './components/SubmitNewsTip';
import BreakingNewsSignup from './components/BreakingNewsSignup';
import AdDisplay from '../../components/AdDisplay';

function NewsHero() {
  return (
    <div className="relative pt-20">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-5">
                <i className="ri-newspaper-line text-teal-400 text-sm"></i>
                <span className="text-teal-300 text-sm font-semibold tracking-wide uppercase">Local News</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Roseville <span className="text-teal-400">Local News</span>
              </h1>
              <p className="text-gray-300 text-lg max-w-xl leading-relaxed">
                Stay informed with the latest updates from Roseville and Placer County, all in one place.
              </p>
              <div className="mt-5">
                <SubmitNewsTip />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
          <div className="h-48 bg-gray-200"></div>
          <div className="p-5">
            <div className="h-4 bg-gray-200 rounded w-20 mb-3"></div>
            <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NewsCard({ article, size = 'default' }: { article: any; size?: 'large' | 'default' | 'compact' }) {
  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  if (size === 'large') {
    return (
      <Link to={`/news/${article.id}`} className="block group cursor-pointer">
        <div className="relative rounded-2xl overflow-hidden h-[420px] shadow-lg">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute top-4 left-4 flex items-center gap-2">
            {article.isBreaking && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded-full animate-pulse whitespace-nowrap">
                <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                BREAKING
              </span>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full mb-3">{article.category}</span>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-teal-300 transition-colors leading-tight">{article.title}</h3>
            <p className="text-white/80 text-sm max-w-2xl line-clamp-2">{article.excerpt}</p>
            <div className="flex items-center gap-4 mt-3 text-white/60 text-xs">
              <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i>{article.timeAgo}</span>
              {article.date && (
                <span className="inline-flex items-center gap-1"><i className="ri-calendar-line"></i>{formatDate(article.date)}</span>
              )}
              {article.location && (
                <span className="inline-flex items-center gap-1"><i className="ri-map-pin-line"></i>{article.location}</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (size === 'compact') {
    return (
      <Link to={`/news/${article.id}`} className="block group cursor-pointer bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all border border-gray-100">
        <div className="flex gap-4">
          <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[11px] text-gray-400">{article.timeAgo}</span>
            </div>
            <h4 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-teal-600 transition-colors leading-snug">{article.title}</h4>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-[11px] text-gray-400">{article.category}</span>
              {article.location && (
                <span className="text-[11px] text-gray-400 inline-flex items-center gap-0.5 truncate"><i className="ri-map-pin-line text-[10px]"></i>{article.location}</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/news/${article.id}`} className="block group cursor-pointer bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-gray-100">
      <div className="relative h-48 overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {article.isBreaking && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded-full whitespace-nowrap">
              <span className="w-1 h-1 bg-white rounded-full animate-pulse"></span>
              BREAKING
            </span>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="text-xs text-gray-400">{article.timeAgo}</span>
        </div>
        <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">{article.category}</span>
        <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2 group-hover:text-teal-600 transition-colors leading-snug line-clamp-2">{article.title}</h3>
        <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">{article.excerpt}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
          {article.date && (
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line text-[11px]"></i>
              {formatDate(article.date)}
            </span>
          )}
          {article.location && (
            <span className="inline-flex items-center gap-1 truncate">
              <i className="ri-map-pin-line text-[11px]"></i>
              {article.location}
            </span>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-teal-600 text-sm font-semibold mt-3 group-hover:gap-2 transition-all">
          Read Full Story <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </Link>
  );
}

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [visibleCount, setVisibleCount] = useState(9);
  
  const { articles, loading, lastUpdated, refresh } = useNewsArticles();

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  useSEO({
    title: 'Roseville Local News | Placer County Updates & Breaking Stories | Roseville.org',
    description: 'Stay informed with real-time Roseville local news and Placer County updates. Breaking news, community stories, city council updates, and more. Updated regularly.',
    keywords: 'Roseville news, Roseville California local news, Placer County news, Roseville breaking news, Roseville updates today',
    canonical: '/news',
    ogImage: 'https://public.readdy.ai/ai/img_res/e857a37d9833389a0bdea4d1fc75d6d5.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: 'Roseville Local News — Placer County Updates',
          description: 'Real-time local news for Roseville and Placer County.',
          url: 'https://roseville.org/news',
          inLanguage: 'en-US',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://roseville.org/' },
              { '@type': 'ListItem', position: 2, name: 'Local News', item: 'https://roseville.org/news' },
            ],
          },
        },
        {
          '@type': 'NewsMediaOrganization',
          name: 'Roseville.org News',
          url: 'https://roseville.org/news',
          description: 'Local news for Roseville, California.',
          areaServed: { '@type': 'City', name: 'Roseville', addressRegion: 'CA' },
        },
      ],
    },
  });

  const filtered = articles.filter((a) => {
    return activeCategory === 'All' || a.category === activeCategory;
  });

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // Build category list from articles
  const categoryNames = ['All', ...Array.from(new Set(articles.map((a) => a.category)))];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <NewsHero />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* News Sidebar Ad */}
        <div className="mb-8">
          <AdDisplay placementId="news-sidebar" />
        </div>

        {/* Auto-refresh status bar */}
        <div className="flex items-center justify-between mb-6 bg-white rounded-xl px-5 py-3 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="text-sm text-gray-600">
              Updated regularly
            </span>
            {lastUpdated && (
              <span className="text-xs text-gray-400">
                Last updated: {formatTime(lastUpdated)}
              </span>
            )}
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-xs text-gray-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>

        {/* Trending Ticker */}
        <TrendingTicker articles={articles} />

        {/* Category Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8" style={{ scrollbarWidth: 'none' }}>
          {categoryNames.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setVisibleCount(9); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-sm text-gray-500 mb-6">
          Showing {visible.length} of {filtered.length} articles
          {activeCategory !== 'All' && <span> in <strong>{activeCategory}</strong></span>}
        </p>

        {loading && articles.length === 0 ? (
          <LoadingSkeleton />
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-newspaper-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your filters to see more results.</p>
          </div>
        ) : (
          <>
            {/* Featured top article */}
            {visible.length > 0 && activeCategory === 'All' && (
              <div className="mb-8">
                <NewsCard article={visible[0]} size="large" />
              </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeCategory === 'All' ? visible.slice(1) : visible).map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-10">
                <button
                  onClick={() => setVisibleCount((c) => c + 6)}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Load More Stories
                  <i className="ri-arrow-down-line"></i>
                </button>
              </div>
            )}
          </>
        )}

        <BreakingNewsSignup />
      </div>

      <Footer />
    </div>
  );
}
